import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Select,
  Button,
  Group,
  Stack,
  Text,
  Badge,
  Paper,
  Divider,
  Alert,
  Loader,
  ScrollArea,
  Tabs,
  Accordion,
} from '@mantine/core';
import {
  IconBrandWhatsapp,
  IconSend,
  IconCheck,
  IconAlertCircle,
  IconExternalLink,
  IconPhone,
  IconBuildingStore,
  IconUser,
  IconTemplate,
  IconMessageDots,
} from '@tabler/icons-react';
import { doc, getDoc, collection, getDocs, query, limit } from 'firebase/firestore';
import { db } from '../../firebase/FirebaseCredential';
import { resolveDynamicVariables, getDynamicListItems } from './dynamicVariables';

export const RealMessageTestModal = ({
  opened,
  onClose,
  nodes = [],
  edges = [],
  tenantId,
  liveCollections = {},
}) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [customMemberName, setCustomMemberName] = useState('John Doe');
  const [sending, setSending] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [recentMembers, setRecentMembers] = useState([]);
  const [gatewayConfig, setGatewayConfig] = useState(null);

  // Template states
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Set default branch when liveCollections change
  useEffect(() => {
    if (liveCollections?.branches && liveCollections.branches.length > 0 && !selectedBranchId) {
      setSelectedBranchId(liveCollections.branches[0].id);
    }
  }, [liveCollections, selectedBranchId]);

  // Fetch company WhatsApp gateway credentials
  useEffect(() => {
    if (!tenantId) return;
    const fetchConfig = async () => {
      try {
        const settingsRef = doc(
          db,
          'companyprofile',
          tenantId,
          'companysettings',
          'whatsappsettings'
        );
        const snap = await getDoc(settingsRef);
        if (snap.exists()) {
          setGatewayConfig(snap.data());
        } else {
          // Check global smsmojo config
          const globalSnap = await getDoc(doc(db, 'smsmojo', 'whatsappconfig'));
          if (globalSnap.exists()) {
            setGatewayConfig(globalSnap.data());
          }
        }
      } catch (err) {
        console.warn('Could not fetch WhatsApp gateway settings:', err);
      }
    };
    fetchConfig();
  }, [tenantId]);

  // Fetch approved templates from SMS Mojo API and Firestore
  useEffect(() => {
    if (!opened) return;
    const loadTemplates = async () => {
      setLoadingTemplates(true);
      const fetched = [];
      const seenNames = new Set();

      const appId = gatewayConfig?.app_id;
      const apiKey = gatewayConfig?.api_key;

      // 1. Fetch from SMS Mojo API directly if credentials exist
      if (appId && apiKey) {
        try {
          const resp = await fetch(`https://workspace.smsmojo.in/api/${appId}/templates/list?status=APPROVED`, {
            headers: {
              Authorization: `Bearer ${apiKey}`,
            },
          });
          const resJson = await resp.json();
          if (resJson && resJson.success && Array.isArray(resJson.data)) {
            resJson.data.forEach((item) => {
              const name = item.template_name || item.name;
              if (name && !seenNames.has(name)) {
                seenNames.add(name);
                fetched.push({
                  id: item.template_id || item.id || name,
                  template_name: name,
                  category: item.category || 'UTILITY',
                  body_text: item.body_text || '',
                  variables: item.variables || [],
                });
              }
            });
          }
        } catch (e) {
          console.warn('Direct SMS Mojo template fetch error:', e);
        }
      }

      // 2. Fetch from company WhatsApp templates in Firestore
      if (tenantId) {
        try {
          const snap = await getDocs(
            collection(db, 'companyprofile', tenantId, 'companysettings', 'whatsappsettings', 'whatsapptemplate')
          );
          snap.forEach((d) => {
            const data = d.data();
            const name = data.template_name;
            if (name && !seenNames.has(name)) {
              seenNames.add(name);
              fetched.push({
                id: d.id,
                template_name: name,
                category: data.template_category || 'UTILITY',
                body_text: data.template_body || '',
                variables: data.fields || [],
              });
            }
          });
        } catch (e) {
          console.warn('Firestore company templates fetch error:', e);
        }
      }

      // 3. Fallback to global templates collection in Firestore
      try {
        const globalSnap = await getDocs(
          collection(db, 'smsmojo', 'whatsappconfig', 'templates')
        );
        globalSnap.forEach((d) => {
          const data = d.data();
          const name = data.template_name || d.id;
          if (name && !seenNames.has(name)) {
            seenNames.add(name);
            fetched.push({
              id: d.id,
              template_name: name,
              category: 'UTILITY',
              body_text: data.template_body || '',
              variables: data.fields || [],
            });
          }
        });
      } catch (e) {
        console.warn('Global templates fetch error:', e);
      }

      // Default fallback templates commonly used in BenchAdmin
      if (fetched.length === 0) {
        const defaultBenchTemplates = [
          { template_name: 'member_registeration', category: 'MEMBERSHIP', body_text: 'Welcome to {{gym_name}}, {{member_name}}! Your plan {{plan_name}} is now active.' },
          { template_name: 'payment_link', category: 'BILLING', body_text: 'Hi {{member_name}}, your invoice for {{plan_name}} is ready.' },
          { template_name: 'class_booking_confirmation', category: 'BOOKING', body_text: 'Your class {{class_name}} is confirmed at {{gym_name}}.' },
        ];
        defaultBenchTemplates.forEach((t) => {
          if (!seenNames.has(t.template_name)) {
            seenNames.add(t.template_name);
            fetched.push(t);
          }
        });
      }

      setTemplates(fetched);
      if (fetched.length > 0 && !selectedTemplateName) {
        setSelectedTemplateName(fetched[0].template_name);
      }
      setLoadingTemplates(false);
    };

    loadTemplates();
  }, [opened, gatewayConfig, tenantId, selectedTemplateName]);

  // Load a few sample members for fast testing
  useEffect(() => {
    if (!opened || !tenantId) return;
    const fetchSampleMembers = async () => {
      try {
        let membersList = [];
        if (selectedBranchId) {
          const mSnap = await getDocs(
            query(collection(db, 'branch', selectedBranchId, 'members'), limit(10))
          );
          mSnap.forEach((d) => {
            const data = d.data();
            const phone = data.mobile_number || data.phone || data.mobile || '';
            const name = [data.first_name, data.last_name].filter(Boolean).join(' ') || data.name || 'Member';
            if (phone) {
              membersList.push({ id: d.id, name, phone });
            }
          });
        }
        setRecentMembers(membersList);
      } catch (e) {
        console.warn('Error fetching sample members:', e);
      }
    };
    fetchSampleMembers();
  }, [opened, tenantId, selectedBranchId]);

  // Identify starting node of the flow
  const startingNode = React.useMemo(() => {
    const triggerNode = nodes.find((n) => n.type === 'triggerNode');
    if (triggerNode) {
      const firstEdge = edges.find((e) => e.source === triggerNode.id);
      if (firstEdge) {
        const targetNode = nodes.find((n) => n.id === firstEdge.target);
        if (targetNode) return targetNode;
      }
    }
    return nodes.find((n) => n.type !== 'triggerNode') || nodes[0];
  }, [nodes, edges]);

  // Current branch details
  const currentBranch = React.useMemo(() => {
    return (
      (liveCollections?.branches || []).find((b) => b.id === selectedBranchId) ||
      liveCollections?.branches?.[0]
    );
  }, [liveCollections, selectedBranchId]);

  // Prepare resolved context
  const fullContext = React.useMemo(() => {
    const ctx = {
      'member.name': customMemberName || 'Valued Member',
      'member.phone': phoneNumber || '',
      'member.branch_name': currentBranch?.name || currentBranch?.title || 'Main Branch',
      'branch.name': currentBranch?.name || currentBranch?.title || 'Main Branch',
      'branch.address': currentBranch?.address || '',
      'branch.phone': currentBranch?.phone || '',
    };

    const firstClass = (liveCollections?.classes || [])[0];
    if (firstClass) {
      ctx['class.class_name'] = firstClass.title;
      ctx['class.trainer_name'] = firstClass.trainer_name || '';
      ctx['class.class_time'] = firstClass.description || '';
    }

    const firstPkg = (liveCollections?.packages || [])[0];
    if (firstPkg) {
      ctx['package.package_name'] = firstPkg.title;
      ctx['package.package_price'] = firstPkg.description || '';
    }

    const firstPrd = (liveCollections?.products || [])[0];
    if (firstPrd) {
      ctx['product.product_name'] = firstPrd.title;
      ctx['product.price'] = firstPrd.description || '';
    }

    return ctx;
  }, [customMemberName, phoneNumber, currentBranch, liveCollections]);

  // Resolve message preview from the flow
  const resolvedContent = React.useMemo(() => {
    if (!startingNode) {
      return { text: 'No message block connected to trigger yet.', buttons: [], isList: false };
    }

    const rawText =
      startingNode.data?.body ||
      startingNode.data?.question ||
      startingNode.data?.prompt ||
      startingNode.data?.label ||
      '';

    const text = resolveDynamicVariables(rawText, fullContext);
    const buttons = startingNode.data?.buttons || [];
    const isList = startingNode.data?.interactiveType === 'list';

    let listItems = [];
    if (isList) {
      listItems = getDynamicListItems(
        startingNode.data?.listSource || 'packages',
        startingNode.data?.listCustomItems || [],
        liveCollections,
        selectedBranchId
      );
    }

    return {
      text,
      buttons,
      isList,
      listItems,
      listCta: startingNode.data?.listCtaText || 'View Options',
      listSectionTitle: startingNode.data?.listSectionTitle || 'Available Options',
    };
  }, [startingNode, fullContext, liveCollections, selectedBranchId]);

  // Build full message string suitable for WhatsApp Web / App
  const fullWhatsAppMessage = React.useMemo(() => {
    let msg = resolvedContent.text;

    if (resolvedContent.buttons && resolvedContent.buttons.length > 0) {
      msg += '\n\n' + resolvedContent.buttons.map((b) => `👉 *[${b.text || b.title}]*`).join('\n');
    }

    if (resolvedContent.isList && resolvedContent.listItems && resolvedContent.listItems.length > 0) {
      msg += `\n\n📋 *${resolvedContent.listSectionTitle}*:\n` +
        resolvedContent.listItems
          .map((item, idx) => `${idx + 1}. *${item.title}* ${item.description ? `(${item.description})` : ''}`)
          .join('\n');
    }

    return msg;
  }, [resolvedContent]);

  // Currently selected template object
  const currentTemplate = React.useMemo(() => {
    return templates.find((t) => t.template_name === selectedTemplateName) || templates[0];
  }, [templates, selectedTemplateName]);

  // Format recipient number (10 digits -> 91...)
  const cleanNumber = (num) => {
    let clean = String(num || '').replace(/\D/g, '');
    if (clean.length === 10) {
      clean = '91' + clean;
    }
    return clean;
  };

  const handleSendViaGateway = async () => {
    const toPhone = cleanNumber(phoneNumber);
    if (!toPhone || toPhone.length < 10) {
      setStatusMessage({
        type: 'error',
        text: 'Please enter a valid 10-digit mobile number.',
      });
      return;
    }

    setSending(true);
    setStatusMessage(null);

    const appId = gatewayConfig?.app_id;
    const apiKey = gatewayConfig?.api_key;

    if (!appId || !apiKey) {
      setStatusMessage({
        type: 'info',
        text: 'WhatsApp Gateway credentials (App ID / API Key) are not configured in settings. You can click "Open in WhatsApp Web" below to send instantly!',
      });
      setSending(false);
      return;
    }

    const templateToUse = selectedTemplateName || templates[0]?.template_name;
    if (!templateToUse) {
      setStatusMessage({
        type: 'error',
        text: 'No approved WhatsApp template available. Meta WABA requires an approved template name for API delivery.',
      });
      setSending(false);
      return;
    }

    try {
      const url = `https://workspace.smsmojo.in/api/${appId}/messages/send`;

      // Build variables matching common WABA template fields
      const memberName = customMemberName || 'Member';
      const branchName = currentBranch?.name || currentBranch?.title || 'Our Club';
      const pkgName = (liveCollections?.packages || [])[0]?.title || 'Standard Plan';
      const className = (liveCollections?.classes || [])[0]?.title || 'Fitness Session';

      const variables = {
        '1': memberName,
        '2': branchName,
        '3': pkgName || className,
        member_name: memberName,
        gym_name: branchName,
        branch_name: branchName,
        plan_name: pkgName,
        class_name: className,
        brandname: branchName,
        amount: '0',
      };

      const payload = {
        to: toPhone,
        type: 'template',
        template_name: templateToUse,
        language: 'en',
        campaign_name: 'Flow Test Real Message',
        variables: variables,
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify(payload),
      });

      const resData = await response.json();
      if (response.ok && resData.success !== false) {
        setStatusMessage({
          type: 'success',
          text: `✅ Real WhatsApp template "${templateToUse}" successfully dispatched to +${toPhone}!`,
        });
      } else {
        setStatusMessage({
          type: 'error',
          text: `Gateway response: ${resData?.message || 'Error sending message'}. You can also use the direct WhatsApp link below.`,
        });
      }
    } catch (err) {
      setStatusMessage({
        type: 'error',
        text: `Network/CORS error communicating with gateway. Use "Open in WhatsApp Web" below to test immediately!`,
      });
    } finally {
      setSending(false);
    }
  };

  const handleOpenDirectWhatsApp = () => {
    const toPhone = cleanNumber(phoneNumber);
    if (!toPhone || toPhone.length < 10) {
      setStatusMessage({
        type: 'error',
        text: 'Please enter a valid 10-digit mobile number first.',
      });
      return;
    }

    const encodedText = encodeURIComponent(fullWhatsAppMessage);
    const waUrl = `https://wa.me/${toPhone}?text=${encodedText}`;
    window.open(waUrl, '_blank');
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group spacing="xs">
          <IconBrandWhatsapp size={22} className="text-emerald-500" />
          <Text weight={700} size="md">Test Flow with Real WhatsApp Message</Text>
        </Group>
      }
      size="lg"
      radius="md"
      centered
    >
      <Stack spacing="md">
        <Text size="xs" color="dimmed">
          Send a real message to any WhatsApp number to test real delivery, branch variables, and member data.
        </Text>

        {/* Inputs row */}
        <Group grow spacing="sm">
          <TextInput
            label="Mobile Number to Test"
            placeholder="e.g. 9876543210"
            required
            icon={<IconPhone size={16} />}
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            size="xs"
          />

          <TextInput
            label="Simulated Member Name"
            placeholder="John Doe"
            icon={<IconUser size={16} />}
            value={customMemberName}
            onChange={(e) => setCustomMemberName(e.target.value)}
            size="xs"
          />
        </Group>

        {/* Branch Context Selector */}
        {(liveCollections?.branches || []).length > 1 && (
          <Select
            label="Branch Perspective"
            placeholder="Choose branch to resolve variables"
            icon={<IconBuildingStore size={16} />}
            value={selectedBranchId}
            onChange={(val) => setSelectedBranchId(val || '')}
            data={(liveCollections?.branches || []).map((b) => ({
              value: b.id,
              label: b.name || b.title || b.id,
            }))}
            size="xs"
          />
        )}

        {/* Quick Member Pick Pills */}
        {recentMembers.length > 0 && (
          <div>
            <Text size="xs" weight={500} color="dimmed" mb={4}>
              Quick Select Gym Member:
            </Text>
            <Group spacing={6}>
              {recentMembers.map((m) => (
                <Badge
                  key={m.id}
                  variant="outline"
                  color="blue"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setPhoneNumber(m.phone);
                    setCustomMemberName(m.name);
                  }}
                >
                  {m.name} ({m.phone})
                </Badge>
              ))}
            </Group>
          </div>
        )}

        <Divider label="Delivery Methods" labelPosition="center" />

        {/* Mode 1: Meta WABA Approved Template */}
        <Paper p="sm" withBorder radius="md" className="bg-slate-50/50 dark:bg-slate-900/50">
          <Stack spacing="xs">
            <Group position="apart">
              <Group spacing="xs">
                <IconTemplate size={16} className="text-blue-600" />
                <Text size="xs" weight={700}>Option A: Official Meta WhatsApp Gateway API</Text>
              </Group>
              <Badge size="xs" color="blue">Requires Approved Template</Badge>
            </Group>
            
            <Text size="xs" color="dimmed">
              Meta WABA strictly requires an approved template for business-initiated API messages. Select an approved template from your SMS Mojo account:
            </Text>

            <Select
              placeholder={loadingTemplates ? "Loading approved templates..." : "Select approved template"}
              value={selectedTemplateName}
              onChange={(val) => setSelectedTemplateName(val || '')}
              data={templates.map((t) => ({
                value: t.template_name,
                label: `${t.template_name} [${t.category || 'UTILITY'}]`,
              }))}
              size="xs"
              searchable
              disabled={loadingTemplates}
            />

            {currentTemplate?.body_text && (
              <Text size="xs" color="dimmed" italic style={{ fontFamily: 'monospace' }}>
                Preview: "{currentTemplate.body_text}"
              </Text>
            )}

            <Button
              color="blue"
              size="xs"
              loading={sending}
              leftIcon={<IconSend size={14} />}
              onClick={handleSendViaGateway}
              style={{ alignSelf: 'flex-start' }}
            >
              Send Template via Gateway
            </Button>
          </Stack>
        </Paper>

        {/* Mode 2: Full Flow Message Direct WhatsApp */}
        <Paper p="sm" withBorder radius="md" className="bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900">
          <Stack spacing="xs">
            <Group position="apart">
              <Group spacing="xs">
                <IconBrandWhatsapp size={16} className="text-emerald-600" />
                <Text size="xs" weight={700} color="green">Option B: Full Flow Message (Instant WhatsApp Web / App)</Text>
              </Group>
              <Badge size="xs" color="green">No Template Needed • 100% Custom Content</Badge>
            </Group>

            <Text size="xs" color="dimmed">
              Opens WhatsApp Web or App directly with your flow's full interactive content, dynamic variables, and buttons formatted and ready to send with 1 click:
            </Text>

            <ScrollArea style={{ maxHeight: 110 }}>
              <Text
                size="xs"
                style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                className="text-slate-800 dark:text-slate-200 bg-white/70 dark:bg-slate-900/70 p-2 rounded border"
              >
                {fullWhatsAppMessage}
              </Text>
            </ScrollArea>

            <Button
              color="teal"
              size="xs"
              leftIcon={<IconExternalLink size={14} />}
              onClick={handleOpenDirectWhatsApp}
              style={{ alignSelf: 'flex-start' }}
            >
              Open in WhatsApp Web / App
            </Button>
          </Stack>
        </Paper>

        {/* Status Alerts */}
        {statusMessage && (
          <Alert
            icon={statusMessage.type === 'success' ? <IconCheck size={16} /> : <IconAlertCircle size={16} />}
            color={statusMessage.type === 'success' ? 'green' : statusMessage.type === 'info' ? 'blue' : 'red'}
            radius="md"
            title={statusMessage.type === 'success' ? 'Message Sent' : statusMessage.type === 'info' ? 'Notice' : 'Error'}
          >
            <Text size="xs">{statusMessage.text}</Text>
          </Alert>
        )}

        <Divider />

        <Group position="right">
          <Button variant="default" size="xs" onClick={onClose}>
            Close
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
